/**
 * The face model, pinned by hash.
 *
 * The library itself is pinned in the page with subresource integrity, but
 * the weights it downloads afterwards were not, and they are just as
 * dangerous. A substituted weight file does not crash anything - it simply
 * returns whatever embedding its author chose. That is enough to mint
 * identities at will, or to claim a stranger's face before they do, and it
 * would leave no trace in the ledger because every resulting claim is
 * perfectly well formed.
 *
 * There is no `integrity` attribute for a fetch that a third-party library
 * makes on its own, so this installs a guard around `fetch` for the model
 * host: every file is hashed as it arrives and compared against the digest
 * recorded here. A mismatch fails loudly rather than quietly producing a
 * face that is not yours.
 *
 * Regenerating these after a version bump is a shell one-liner; see
 * README, "Pinning the face model".
 */

const MODEL_DIGESTS: Record<string, string> = {
  'ssd_mobilenetv1_model-weights_manifest.json': 'sha384-fmysjuNDnSIM5gqRwPVEGA1/0w5Ok0SqyKNnnaNTWojtlqAtwqDRz/44hF3HkhRr',
  'ssd_mobilenetv1_model.bin': 'sha384-2spbycjYagJihJ9ctoisEt/ALI+VrSvaIRMKYUAa7dQITarhWrP8n9lmrUkw7UIW',
  'face_landmark_68_model-weights_manifest.json': 'sha384-jyeQciMhD4TgOdqkrLOmOuIU9PB8iWFiJnK+FXAu91aMTak51qDiuU+B7yYl8MOK',
  'face_landmark_68_model.bin': 'sha384-oX0nknyg2Op36WlwAOun3YsSgFLNplrl/uJbo9A1b4jKez4oin2i1NkzULnkmL+Z',
  'face_recognition_model-weights_manifest.json': 'sha384-0/BxZ0200/qGz3ORL6RPhCi2+3YNPcL8q2IUqGPKdicLd6GPLOeyz2mGFFWhdcbD',
  'face_recognition_model.bin': 'sha384-pbcnzam7/sfrZVQx0ivS+lBKJzy/MRGQuIKoj8N4DlRG6xYmS+CGeDCo6DL1/XS/',
  'tiny_face_detector_model-weights_manifest.json': 'sha384-pv5O6FHygbu0YHC1SE1HT8UoirI7XZHizelMsZEuqwx05LlqXklbPC09P3kSOXyH',
  'tiny_face_detector_model.bin': 'sha384-bHZoLhI+HSjugOsPHIOQJgnKxzpbiKzHS+l6eXbGRgm6mLboHYtWWuvNpVwriT2V',
};

export class ModelIntegrityError extends Error {}

async function sha384Base64(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-384', bytes);
  let bin = '';
  for (const b of new Uint8Array(digest)) bin += String.fromCharCode(b);
  return 'sha384-' + btoa(bin);
}

let installed = false;

/**
 * Verify every model file this page downloads.
 *
 * Wraps `fetch` once. Requests that are not model files pass through
 * untouched, and an unrecognised file under the model path is refused
 * rather than allowed - an unknown weight file is exactly what a
 * substitution looks like.
 */
export function enforceModelIntegrity(modelUrl: string): void {
  if (installed || typeof fetch !== 'function') return;
  installed = true;

  const prefix = modelUrl.endsWith('/') ? modelUrl : modelUrl + '/';
  const original = fetch.bind(globalThis);

  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const bare = url.split('?')[0];

    if (!bare.startsWith(prefix)) return original(input as RequestInfo, init);
    const name = bare.slice(prefix.length);
    const expected = MODEL_DIGESTS[name];
    const what = `the face model (${name})`;

    if (!expected) {
      throw new ModelIntegrityError(`refusing an unrecognised model file: ${bare}`);
    }

    const response = await original(input as RequestInfo, init);
    if (!response.ok) return response;

    const bytes = await response.arrayBuffer();
    const actual = await sha384Base64(bytes);
    if (actual !== expected) {
      throw new ModelIntegrityError(
        `${what} failed its integrity check. Someone may be substituting it; refusing to continue.`,
      );
    }
    // Hand back an untouched copy: the body was consumed to hash it.
    //
    // Only the content type is carried over. `content-encoding` and
    // `content-length` describe the bytes *on the wire*, and these bytes
    // have already been decoded - copying those headers onto a body the
    // browser will not decode again describes an 11 MB WebAssembly module
    // as its own compressed size, which is exactly the sort of thing that
    // breaks streaming instantiation and nothing else.
    const type = response.headers.get('content-type');
    return new Response(bytes, {
      status: response.status,
      statusText: response.statusText,
      headers: type ? { 'content-type': type } : undefined,
    });
  };
}


/** Exposed so the pinning can be tested without a network. */
export const PINNED_MODEL_FILES = Object.keys(MODEL_DIGESTS);
export const MODEL_DIGEST_FOR = (name: string): string | undefined => MODEL_DIGESTS[name];

